import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_PERMISSIONS, transformPermissions } from '@vision/ui/constants';
import {
  VisionDateFormatType,
  VisionNumberFormatType,
  VisionPercentFormatType,
  VisionTimeFormatType,
} from '@vision/ui/enums';
import i18n, { SUPPORTED_LANGUAGES } from '@vision/ui/i18n';
import { LocalizationSettings, Nullable, Permission, User, UserWithOtp } from '@vision/ui/interfaces';
import { AccountService, AuthService, UsersService } from '@vision/ui/services';
import { ensureArray, mergeDeep, selectDefaultAssignedNode } from '@vision/ui/utils';

function updateCurrentUserForClojureApp(state: AuthStateType) {
  // Clojure app "currentUser" değerini okuyarak authentication yapıyor
  localStorage.setItem(
    'currentUser',
    JSON.stringify({
      ...state.user,
      selectedAccountId: state.selectedAccountId,
    } as User),
  );

  localStorage.setItem('localizationSettings', JSON.stringify(state.localizationSettings));
}

function getLocalizationSettings(localization: string): LocalizationSettings {
  if (typeof localization === 'string') {
    const localizationSettingsConverted = JSON.parse(localization) as LocalizationSettings;

    return {
      date_format: localizationSettingsConverted.date_format,
      number_format: localizationSettingsConverted.number_format,
      percent_format: localizationSettingsConverted.percent_format,
      time_format: localizationSettingsConverted.time_format,
    };
  }

  const localizationSettings = localStorage.getItem('localizationSettings');

  if (localizationSettings) {
    return JSON.parse(localizationSettings);
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (timezone === 'America/New_York') {
    return {
      date_format: VisionDateFormatType.US,
      number_format: VisionNumberFormatType.PERIOD,
      percent_format: VisionPercentFormatType.COMMA_RIGHT,
      time_format: VisionTimeFormatType.H12,
    };
  }

  if (timezone === 'Europe/Istanbul') {
    return {
      date_format: VisionDateFormatType.TR,
      number_format: VisionNumberFormatType.COMMA,
      percent_format: VisionPercentFormatType.COMMA_LEFT,
      time_format: VisionTimeFormatType.H24,
    };
  }

  return {
    date_format: VisionDateFormatType.TR,
    number_format: VisionNumberFormatType.COMMA,
    percent_format: VisionPercentFormatType.DOT_RIGHT,
    time_format: VisionTimeFormatType.H24,
  };
}

function changeLanguageByUserLang(languageCode: string) {
  const userLanguage = languageCode.toLowerCase();
  const supported = SUPPORTED_LANGUAGES.some((lang) => lang === userLanguage);
  if (supported) {
    i18n.changeLanguage(userLanguage);
  }
}

function patchAuthentication(state: AuthStateType, payload: User) {
  const selectedAccountId = state.selectedAccountId || selectDefaultAssignedNode(payload).id;

  state.id = payload.id;
  state.token = payload.token;
  state.user = payload;
  state.permissions = transformPermissions(payload.permissions);
  state.selectedAccountId = selectedAccountId;
  state.otpUserInfo = null;

  state.localizationSettings = getLocalizationSettings(state.user.localization);

  updateCurrentUserForClojureApp(state);
  changeLanguageByUserLang(payload.language.code);
}

export interface AuthStateType {
  id: Nullable<string>;
  otpUserInfo: {
    email: string;
    password: string;
  };
  permissions: Permission;
  selectedAccountId: string;
  token: Nullable<string>;
  user: User;
  securitySettingsModalVisible?: boolean;
  showReportPageAlertMessage?: boolean;
  localizationSettings?: LocalizationSettings;
}

const initialState: AuthStateType = {
  id: null,
  otpUserInfo: null,
  permissions: DEFAULT_PERMISSIONS,
  selectedAccountId: null,
  token: null,
  user: {} as User,
  showReportPageAlertMessage: false,
  localizationSettings: null,
};

export const AuthState = createSlice({
  name: 'AuthState',
  initialState,
  reducers: {
    logoutAction: (state) => {
      Object.assign(state, initialState);
      localStorage.removeItem('currentUser');
    },

    updateOtpUserInfoAction: (state, action: PayloadAction<{ email: string; password: string }>) => {
      state.otpUserInfo = action.payload;
    },

    changeSelectedAccountAction: (state, { payload: id }: PayloadAction<string>) => {
      const node = ensureArray(state.user.assigned_nodes).find((item) => item.id === id);
      if (node) {
        state.selectedAccountId = id;
        state.user.node = node;

        updateCurrentUserForClojureApp(state);
      }
    },

    changeSecuritySettingsModalVisibleAction: (state, { payload: visible }: PayloadAction<boolean>) => {
      state.securitySettingsModalVisible = visible;
    },

    setShowPageTopMessageAction: (state, { payload: visible }: PayloadAction<boolean>) => {
      state.showReportPageAlertMessage = visible;
    },

    setBaseAuthConfigAction: (
      state,
      {
        payload: { id, token, selectedAccountId },
      }: PayloadAction<{ id: string; token: string; selectedAccountId: string }>,
    ) => {
      state.token = token;
      state.id = id;
      state.selectedAccountId = selectedAccountId;
    },
    setLocalizationSettingsAction: (state, { payload }: PayloadAction<LocalizationSettings>) => {
      state.localizationSettings = payload;
      localStorage.setItem('localizationSettings', JSON.stringify(payload));
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(AuthService.endpoints.login.matchFulfilled, (state, { payload }) => {
      if ((payload as UserWithOtp).otp_required) {
        return;
      }
      patchAuthentication(state, payload as User);
    });
    builder.addMatcher(AuthService.endpoints.authenticate.matchFulfilled, (state, { payload }) => {
      patchAuthentication(state, payload);
    });
    builder.addMatcher(AuthService.endpoints.validateOtp.matchFulfilled, (state, { payload }) => {
      patchAuthentication(state, payload);
    });
    builder.addMatcher(AccountService.endpoints.accountCompleteLogin.matchFulfilled, (state, { payload }) => {
      patchAuthentication(state, payload);
    });
    builder.addMatcher(AuthService.endpoints.updateAuthUser.matchFulfilled, (state, { payload }) => {
      state.user = mergeDeep(state.user, payload);
    });
    builder.addMatcher(AuthService.endpoints.loginAsUser.matchFulfilled, (state, { payload }) => {
      patchAuthentication(state, payload as User);
    });
    builder.addMatcher(AuthService.endpoints.changePassword.matchFulfilled, (state, { payload }) => {
      patchAuthentication(state, payload as User);
    });
    builder.addMatcher(AuthService.endpoints.uploadProfilePicture.matchFulfilled, (state, { payload }) => {
      state.user.avatar = payload.avatar;
    });
    builder.addMatcher(AuthService.endpoints.deleteProfilePicture.matchFulfilled, (state, { payload }) => {
      state.user.avatar = payload.avatar;
    });
    builder.addMatcher(UsersService.endpoints.updateUserMenu.matchFulfilled, (state, { payload }) => {
      state.user.menu = payload.menu;
    });
  },
});

export const {
  logoutAction,
  updateOtpUserInfoAction,
  changeSelectedAccountAction,
  changeSecuritySettingsModalVisibleAction,
  setShowPageTopMessageAction,
  setBaseAuthConfigAction,
  setLocalizationSettingsAction,
} = AuthState.actions;
